import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import {
  ArrowRight,
  Calendar,
  Trophy,
  ShieldCheck,
  Star,
  CreditCard,
} from "lucide-react";
import { RAFFLE } from "@/lib/raffleData";
import { useSiteImages } from "@/lib/siteImages";
import Confetti from "@/components/raffle/Confetti";

const line = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] },
  }),
};

const fade = {
  hidden: { opacity: 0, y: 22 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: 0.55 + i * 0.1, ease: [0.16, 1, 0.3, 1] },
  }),
};

const cardIn = {
  hidden: { opacity: 0, y: 40, scale: 0.96 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.8, delay: 0.7 + i * 0.15, ease: [0.16, 1, 0.3, 1] },
  }),
};

/* ---------- Custom icons (inline SVG to match reference) ---------- */

function TicketIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 64 40" className={className} aria-hidden="true">
      <path
        d="M6 6 h20 v4 h4 v-4 h28 a2 2 0 0 1 2 2 v8 a4 4 0 0 0 0 8 v8 a2 2 0 0 1 -2 2 h-28 v-4 h-4 v4 h-20 a2 2 0 0 1 -2 -2 v-8 a4 4 0 0 0 0 -8 v-8 a2 2 0 0 1 2 -2 z"
        fill="currentColor"
      />
      <path d="M26 12 h4 v3 h-4 z M26 18 h4 v3 h-4 z M26 24 h4 v3 h-4 z" fill="#0A2818" />
    </svg>
  );
}

function WhatsAppIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true" fill="currentColor">
      <path d="M20.52 3.48A11.86 11.86 0 0 0 12.06 0C5.55 0 .27 5.28.27 11.8c0 2.08.55 4.11 1.6 5.9L0 24l6.47-1.7a11.83 11.83 0 0 0 5.59 1.42h.01c6.51 0 11.79-5.28 11.79-11.8 0-3.15-1.23-6.11-3.34-8.44zM12.07 21.72h-.01a9.9 9.9 0 0 1-5.04-1.38l-.36-.21-3.84 1.01 1.02-3.74-.24-.38a9.87 9.87 0 0 1-1.51-5.22c0-5.44 4.43-9.87 9.87-9.87 2.64 0 5.12 1.03 6.98 2.9a9.83 9.83 0 0 1 2.89 6.99c0 5.44-4.42 9.9-9.86 9.9zm5.4-7.4c-.3-.15-1.75-.86-2.03-.96-.27-.1-.47-.15-.67.15s-.77.96-.94 1.16c-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47s1.07 2.87 1.22 3.07c.15.2 2.1 3.2 5.09 4.49.71.31 1.27.49 1.7.63.71.23 1.36.19 1.87.12.57-.08 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35z" />
    </svg>
  );
}

function LoteIcon({ className = "" }) {
  return (
    <svg viewBox="0 0 64 64" className={className} aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
      {/* mountains */}
      <path d="M6 40 L18 24 L28 36 L38 20 L58 40" />
      {/* tree */}
      <circle cx="32" cy="44" r="6" />
      <path d="M32 50 v6" />
      {/* ground line */}
      <path d="M4 56 h56" />
    </svg>
  );
}

function ChevroletBowtie({ className = "" }) {
  // Stylized bowtie mark
  return (
    <svg viewBox="0 0 80 40" className={className} aria-hidden="true">
      <defs>
        <linearGradient id="bowGold" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#FFF5CC" />
          <stop offset="50%" stopColor="#F0B91B" />
          <stop offset="100%" stopColor="#B8860B" />
        </linearGradient>
      </defs>
      <path
        d="M4 12 h20 l6 -6 h20 l6 6 h20 v16 h-20 l-6 6 h-20 l-6 -6 h-20 z"
        fill="url(#bowGold)"
        stroke="#8B6914"
        strokeWidth="1.2"
      />
      <path d="M8 16 h68 v8 h-68 z" fill="none" />
    </svg>
  );
}

/* --------------------------------------------------------------- */

export default function Hero() {
  const ref = useRef(null);
  const images = useSiteImages();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const yBg = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const opacityContent = useTransform(scrollYProgress, [0, 0.85], [1, 0]);

  return (
    <section
      ref={ref}
      id="top"
      className="relative min-h-[100svh] flex items-stretch overflow-hidden pt-24 pb-40 md:pb-44"
      data-testid="hero-section"
    >
      {/* Background image with parallax */}
      <motion.div
        style={{ y: yBg, backgroundImage: `url(${images.hero})` }}
        className="absolute inset-0 bg-cover bg-center"
      />
      {/* Dark overlay for legibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#051A0F]/85 via-[#051A0F]/50 to-[#051A0F]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#051A0F] via-transparent to-[#051A0F]/60" />
      {/* Radial gold glow behind title */}
      <div className="absolute -top-32 left-1/4 w-[560px] h-[560px] rounded-full bg-[#F0B91B]/25 blur-[120px]" />
      {/* Confetti falling */}
      <Confetti count={22} />

      <motion.div
        style={{ opacity: opacityContent }}
        className="relative z-10 w-full max-w-7xl mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center"
      >
        {/* LEFT COLUMN */}
        <div className="lg:col-span-7 flex flex-col items-start text-left">
          {/* Title — both lines gold with shimmer */}
          <motion.h1
            initial="hidden"
            animate="show"
            className="font-bebas text-[3.75rem] sm:text-7xl md:text-8xl lg:text-[9.5rem] leading-[0.82] tracking-wide"
            data-testid="hero-title"
          >
            <span className="mask-line">
              <motion.span
                variants={line}
                custom={0}
                className="relative inline-flex items-start gap-2 drop-shadow-[0_6px_18px_rgba(0,0,0,0.7)]"
              >
                <span className="gold-shimmer">GRAN</span>
                <Star
                  className="w-6 h-6 md:w-9 md:h-9 text-[#FFD700] fill-[#FFD700] mt-2 md:mt-3 drop-shadow-[0_0_18px_rgba(255,215,0,0.95)]"
                  strokeWidth={1}
                />
              </motion.span>
            </span>
            <span className="mask-line">
              <motion.span variants={line} custom={1} className="block gold-shimmer drop-shadow-[0_6px_18px_rgba(0,0,0,0.7)]">
                PROYECTO
              </motion.span>
            </span>
          </motion.h1>

          {/* Tagline pill */}
          <motion.div
            variants={fade}
            custom={0}
            initial="hidden"
            animate="show"
            className="mt-5 inline-flex items-center gap-2 px-6 py-2.5 rounded-md bg-[#0A2818] border-2 border-[#F0B91B]/60 shadow-[0_10px_30px_-12px_rgba(0,0,0,0.9)]"
            data-testid="hero-tagline"
          >
            <span className="font-bebas text-lg md:text-2xl tracking-[0.18em] text-[#FDFBF7]">
              TU FICHA,
            </span>
            <span className="font-bebas text-lg md:text-2xl tracking-[0.18em] text-[#FDFBF7]">
              TU OPORTUNIDAD
            </span>
          </motion.div>

          {/* Description */}
          <motion.p
            variants={fade}
            custom={1}
            initial="hidden"
            animate="show"
            className="mt-5 text-base md:text-lg text-[#FDFBF7]/95 max-w-xl leading-relaxed font-outfit drop-shadow-[0_2px_10px_rgba(0,0,0,0.75)]"
          >
            Dos premios increíbles en un solo sorteo.{" "}
            <br className="hidden sm:block" />
            Más de{" "}
            <span className="text-[#F0B91B] font-bold">
              ${RAFFLE.earlyPrize}
            </span>{" "}
            en premios.
          </motion.p>

          {/* CTA panel */}
          <motion.div
            variants={fade}
            custom={2}
            initial="hidden"
            animate="show"
            className="mt-8 w-full max-w-sm rounded-2xl border-2 border-[#F0B91B]/50 bg-[#051A0F]/90 backdrop-blur-md p-5 md:p-6 shadow-[0_25px_60px_-20px_rgba(0,0,0,0.95)]"
          >
            <div className="flex items-start gap-3">
              <TicketIcon className="w-12 h-8 md:w-14 md:h-9 text-[#F0B91B] shrink-0 mt-0.5 drop-shadow-[0_0_12px_rgba(240,185,27,0.5)]" />
              <div className="min-w-0">
                <h3 className="font-bebas text-xl md:text-[1.6rem] tracking-wider text-[#FDFBF7] leading-tight">
                  ADQUIERE TU FICHA{" "}
                  <span className="gold-shimmer">Y PARTICIPA</span>
                </h3>
                <p className="text-[10px] md:text-xs uppercase tracking-[0.2em] text-[#FDFBF7]/70 mt-1.5 font-outfit font-semibold">
                  ¡La suerte puede ser tuya!
                </p>
              </div>
            </div>

            <div className="mt-5 flex flex-col gap-2.5">
              <a
                href="#elegir-ficha"
                className="group inline-flex items-center justify-between gap-3 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] pl-4 pr-3 py-3 rounded-full font-bebas text-base md:text-lg tracking-wider shadow-[0_15px_40px_-10px_rgba(240,185,27,0.75)] hover:shadow-[0_20px_50px_-8px_rgba(240,185,27,0.95)] transition-all hover:-translate-y-0.5"
                data-testid="hero-cta-buy"
              >
                <span className="flex items-center gap-2.5">
                  <WhatsAppIcon className="w-5 h-5" />
                  ADQUIRIR POR WHATSAPP
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
              <a
                href="#elegir-ficha"
                className="group inline-flex items-center justify-between gap-3 text-[#F0B91B] hover:text-[#FFD700] pl-4 pr-3 py-2.5 border-2 border-[#F0B91B]/50 hover:border-[#F0B91B] rounded-full font-bebas text-sm md:text-base tracking-wider transition-all"
                data-testid="hero-cta-pay"
              >
                <span className="flex items-center gap-2.5">
                  <CreditCard className="w-4 h-4" />
                  PAGAR EN LÍNEA
                </span>
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </a>
            </div>
          </motion.div>
        </div>

        {/* RIGHT COLUMN — Prize cards */}
        <div className="lg:col-span-5 relative flex flex-col items-center lg:items-end">
          <motion.div
            variants={cardIn}
            custom={0}
            initial="hidden"
            animate="show"
            className="relative w-full max-w-md"
          >
            {/* PREMIOS tab */}
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
              <div className="bg-[#F0B91B] text-[#051A0F] px-8 py-1.5 rounded-md font-bebas text-sm md:text-base tracking-[0.3em] shadow-[0_10px_24px_-8px_rgba(240,185,27,0.9)]">
                PREMIOS
              </div>
            </div>

            <div className="relative rounded-2xl border-2 border-[#F0B91B]/60 bg-[#0A2818]/95 backdrop-blur-md p-5 md:p-6 pt-10 shadow-[0_30px_70px_-25px_rgba(0,0,0,0.95)]">
              <div className="grid grid-cols-2 gap-4 md:gap-6 relative">
                {/* Divider */}
                <div className="absolute left-1/2 top-2 bottom-2 w-px bg-gradient-to-b from-transparent via-[#F0B91B]/50 to-transparent" />

                {/* Prize 1 — Lote */}
                <div className="text-center flex flex-col items-center pr-2">
                  <LoteIcon className="w-14 h-14 md:w-16 md:h-16 text-[#F0B91B] mb-2 drop-shadow-[0_0_10px_rgba(240,185,27,0.55)]" />
                  <div className="font-bebas text-3xl md:text-4xl text-[#FDFBF7] tracking-wider leading-none">
                    LOTE
                  </div>
                  <div className="font-bebas text-4xl md:text-5xl gold-shimmer leading-none mt-1">
                    {RAFFLE.lote.area}
                    <span className="text-2xl md:text-3xl align-super ml-0.5">m²</span>
                  </div>
                  <div className="mt-2 text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#FDFBF7]/80 font-outfit font-semibold">
                    {RAFFLE.location}
                  </div>
                </div>

                {/* Prize 2 — Car */}
                <div className="text-center flex flex-col items-center pl-2">
                  <ChevroletBowtie className="w-16 h-8 md:w-20 md:h-10 mb-2 drop-shadow-[0_0_10px_rgba(240,185,27,0.55)]" />
                  <div className="font-bebas text-xl md:text-2xl text-[#FDFBF7] tracking-wider leading-none">
                    CHEVROLET
                  </div>
                  <div className="font-bebas text-2xl md:text-3xl gold-shimmer leading-tight mt-1">
                    SPARK LIFE
                  </div>
                  <div className="mt-1 font-bebas text-xl md:text-2xl text-[#FDFBF7]">
                    2015
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Circular seal — Más de $10 millones */}
          <motion.div
            variants={cardIn}
            custom={1}
            initial="hidden"
            animate="show"
            className="mt-6 lg:mt-8 lg:self-end"
          >
            <div className="relative w-40 h-40 md:w-48 md:h-48">
              {/* Rotating outer ring */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 24, ease: "linear", repeat: Infinity }}
                className="absolute inset-0 rounded-full border-2 border-dashed border-[#F0B91B]/60"
              />
              {/* Zig-zag/scallop edge */}
              <div className="absolute inset-1 rounded-full bg-[#0A2818] border-4 border-[#F0B91B] flex flex-col items-center justify-center shadow-[0_20px_50px_-15px_rgba(240,185,27,0.75)]">
                <span className="text-[9px] md:text-[10px] uppercase tracking-[0.3em] text-[#F0B91B] font-outfit font-bold">
                  Más de
                </span>
                <span className="font-bebas text-4xl md:text-5xl gold-shimmer leading-none mt-0.5">
                  $10
                </span>
                <span className="font-bebas text-sm md:text-base text-[#FDFBF7] tracking-widest mt-0.5">
                  MILLONES
                </span>
              </div>
              {/* Bottom ribbon */}
              <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 z-10">
                <div className="relative bg-[#F0B91B] text-[#051A0F] px-4 py-1 font-bebas text-[10px] md:text-xs tracking-[0.25em] shadow-[0_8px_20px_-8px_rgba(240,185,27,0.9)]"
                     style={{
                       clipPath:
                         "polygon(6px 0, calc(100% - 6px) 0, 100% 50%, calc(100% - 6px) 100%, 6px 100%, 0 50%)",
                     }}
                >
                  EN PREMIOS
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* BOTTOM STATS STRIP */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 1 }}
        className="absolute bottom-4 md:bottom-6 inset-x-0 z-10 px-4 md:px-8"
      >
        <div className="max-w-7xl mx-auto rounded-2xl border border-[#F0B91B]/35 bg-[#0A2818]/92 backdrop-blur-md shadow-[0_20px_50px_-20px_rgba(0,0,0,0.9)]">
          <div className="grid grid-cols-2 lg:grid-cols-4 divide-y lg:divide-y-0 lg:divide-x divide-[#F0B91B]/20">
            <StatItem
              icon={<TicketIcon className="w-10 h-6 md:w-12 md:h-8" />}
              label="Valor Ficha"
              value={`$${RAFFLE.fichaPrice}`}
              testId="stat-ficha"
            />
            <StatItem
              icon={<Calendar className="w-7 h-7 md:w-8 md:h-8" strokeWidth={2.2} />}
              label="Entrega Anticipada"
              value={RAFFLE.earlyDateShort.toUpperCase()}
              testId="stat-early"
            />
            <StatItem
              icon={<Trophy className="w-7 h-7 md:w-8 md:h-8" strokeWidth={2.2} />}
              label="Sorteo Final"
              value={RAFFLE.drawDateShort.toUpperCase()}
              testId="stat-draw"
            />
            <StatItem
              icon={<ShieldCheck className="w-7 h-7 md:w-8 md:h-8" strokeWidth={2.2} />}
              label="Transparencia y Confianza"
              bullets={["Entregas 100% verificables", "Proceso legal y autorizado"]}
              testId="stat-trust"
            />
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function StatItem({ icon, label, value, bullets, testId }) {
  return (
    <div className="flex items-center gap-3 md:gap-4 px-4 py-4 md:px-6 md:py-5" data-testid={testId}>
      <div className="text-[#F0B91B] shrink-0">{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#F0B91B] font-outfit font-bold">
          {label}
        </div>
        {value && (
          <div className="font-bebas text-2xl md:text-3xl text-[#FDFBF7] tracking-wide leading-tight mt-0.5">
            {value}
          </div>
        )}
        {bullets && (
          <ul className="mt-1 space-y-0.5">
            {bullets.map((b, i) => (
              <li
                key={i}
                className="text-[10px] md:text-xs text-[#FDFBF7]/85 font-outfit flex items-center gap-1.5"
              >
                <span className="w-1 h-1 rounded-full bg-[#F0B91B]" />
                {b}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
